import React, { useState, useEffect, useCallback, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Container, Row, Col, Card, CardBody, Button, Spinner, Alert, Modal, ModalHeader, ModalBody } from 'reactstrap';
import Text from './Text';
import Messages from '../Shared/Messages';
import appDownloadConfig from '../config/appDownloadConfig';
import { getDeviceInfo, checkAppInstalled as checkAppInstalledDeepLink, setDeviceSimulation, clearDeviceSimulation, getSimulationMode } from '../utils/deviceDetection';
import './DownloadApp.scss';

const DownloadApp = ({ userConfirmation, confirmSuccessMessage }) => {
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [copiedLink, setCopiedLink] = useState(null);
  const [installStatus, setInstallStatus] = useState(null);
  const [checkingApp, setCheckingApp] = useState(false);
  const [appInstalled, setAppInstalled] = useState(null);
  const [pwaInstalled, setPwaInstalled] = useState(false);
  const [checkingPwaInstalled, setCheckingPwaInstalled] = useState(true);
  const [isRunningAsPWA, setIsRunningAsPWA] = useState(false);
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const [instructionsModalPlatform, setInstructionsModalPlatform] = useState(null);

  // Refs for timeout cleanup
  const installTimeoutRef = useRef(null);
  const copyLinkTimeoutRef = useRef(null);

  useEffect(() => { document.title = "Download App — myPatientSpace"; }, []);

  // Initialize device detection
  useEffect(() => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const currentSimulation = getSimulationMode();

    // Default to desktop simulation on localhost if no simulation is set
    if (isLocalhost && !currentSimulation) {
      setDeviceSimulation('desktop');
      return; // Will reload and re-run this effect
    }

    const info = getDeviceInfo();
    setDeviceInfo(info);

    // Log analytics
    if (appDownloadConfig.features.enableAnalytics) {
      trackEvent('download_page_view', {
        device_type: info.deviceType,
        is_mobile: info.isMobile,
        browser: getBrowserName(info),
      });
    }
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (installTimeoutRef.current) clearTimeout(installTimeoutRef.current);
      if (copyLinkTimeoutRef.current) clearTimeout(copyLinkTimeoutRef.current);
    };
  }, []);

  // Handle PWA install prompt and check if already installed
  useEffect(() => {
    let promptReceived = false;
    let pollInterval = null;

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      promptReceived = true;
      // If we receive this event, PWA is NOT installed
      setPwaInstalled(false);
      setCheckingPwaInstalled(false);
    };

    const handleAppInstalled = () => {
      setPwaInstalled(true);
      setDeferredPrompt(null);
    };

    // Check if PWA is already installed
    const checkPwaInstalled = () => {
      // Check using display-mode media query (running inside PWA)
      if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
        setPwaInstalled(true);
        setCheckingPwaInstalled(false);
        setIsRunningAsPWA(true); // Track that we're running as PWA
        // Don't set installStatus here - success should only show after actual installation
        // Clear polling immediately when PWA detected
        if (pollInterval) {
          clearInterval(pollInterval);
          pollInterval = null;
        }
        return true;
      }
      return false;
    };

    // Listen for display mode changes (when user clicks "Open in app")
    const displayModeQuery = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = (e) => {
      if (e.matches) {
        // Just switched to PWA mode
        setPwaInstalled(true);
        setIsRunningAsPWA(true);
        // Don't set installStatus here - user didn't just install
        // Clear polling when mode changes
        if (pollInterval) {
          clearInterval(pollInterval);
          pollInterval = null;
        }
      }
    };

    checkPwaInstalled();
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Add listener for display mode changes
    if (displayModeQuery.addEventListener) {
      displayModeQuery.addEventListener('change', handleDisplayModeChange);
    } else if (displayModeQuery.addListener) {
      // Fallback for older browsers
      displayModeQuery.addListener(handleDisplayModeChange);
    }

    // Poll for display mode changes (in case change event doesn't fire)
    // This handles the case when clicking "Open in app" from browser
    pollInterval = setInterval(() => {
      checkPwaInstalled();
    }, 500);

    // If no beforeinstallprompt event after a delay, PWA is likely installed
    // (Chrome doesn't fire this event if PWA is already installed)
    const timeoutId = setTimeout(() => {
      if (!promptReceived && !window.matchMedia('(display-mode: standalone)').matches) {
        // No prompt received and not running as PWA - assume installed
        setPwaInstalled(true);
      }
      setCheckingPwaInstalled(false);
      if (pollInterval) clearInterval(pollInterval); // Stop polling after initial check
    }, 1500);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      if (displayModeQuery.removeEventListener) {
        displayModeQuery.removeEventListener('change', handleDisplayModeChange);
      } else if (displayModeQuery.removeListener) {
        displayModeQuery.removeListener(handleDisplayModeChange);
      }
      clearTimeout(timeoutId);
      clearInterval(pollInterval);
    };
  }, []);

  // Check if app is already installed
  useEffect(() => {
    if (!deviceInfo || !appDownloadConfig.features.enableDeepLinking) return;

    const checkInstalled = async () => {
      setCheckingApp(true);
      try {
        if (deviceInfo.isIOS && appDownloadConfig.ios.enabled) {
          // For iOS, we can't reliably detect, so skip
          setAppInstalled(null);
        } else if (deviceInfo.isAndroid && appDownloadConfig.android.enabled) {
          // For Android, check using getInstalledRelatedApps API
          if ('getInstalledRelatedApps' in navigator) {
            const relatedApps = await navigator.getInstalledRelatedApps();
            setAppInstalled(relatedApps.length > 0);
          }
        }
      } catch (error) {
        console.error('Error checking app installation:', error);
      } finally {
        setCheckingApp(false);
      }
    };

    checkInstalled();
  }, [deviceInfo]);

  // Helper: Get browser name
  const getBrowserName = (info) => {
    if (info.isChrome) return 'Chrome';
    if (info.isSafari) return 'Safari';
    if (info.isFirefox) return 'Firefox';
    if (info.isEdge) return 'Edge';
    return 'Unknown';
  };

  // Helper: Track analytics events
  const trackEvent = (eventName, properties = {}) => {
    if (!appDownloadConfig.features.enableAnalytics) return;

    // Integrate with your analytics service (Google Analytics, Mixpanel, etc.)
    if (window.gtag) {
      window.gtag('event', eventName, properties);
    }

    console.log('Analytics Event:', eventName, properties);
  };

  // Handle PWA install
  const handlePWAInstall = useCallback(async () => {
    if (!deferredPrompt) {
      // Fallback: Just open the site
      window.open(appDownloadConfig.pwa.url, '_self');
      trackEvent('pwa_install_attempted', { method: 'fallback' });
      return;
    }

    try {
      setInstallStatus('installing');
      deferredPrompt.prompt();

      const choiceResult = await deferredPrompt.userChoice;

      if (choiceResult.outcome === 'accepted') {
        setInstallStatus('success');
        trackEvent('pwa_install_accepted');
        // Don't clear success status - let user click the login link
      } else {
        setInstallStatus('cancelled');
        trackEvent('pwa_install_cancelled');
        // Clear cancelled status after 3 seconds
        if (installTimeoutRef.current) clearTimeout(installTimeoutRef.current);
        installTimeoutRef.current = setTimeout(() => setInstallStatus(null), 3000);
      }

      setDeferredPrompt(null);
    } catch (error) {
      console.error('PWA install error:', error);
      setInstallStatus('error');
      // Clear error status after 3 seconds
      if (installTimeoutRef.current) clearTimeout(installTimeoutRef.current);
      installTimeoutRef.current = setTimeout(() => setInstallStatus(null), 3000);
    }
  }, [deferredPrompt]);

  // Handle opening the installed PWA
  const handleOpenPWA = useCallback(() => {
    trackEvent('pwa_open_clicked');

    if (isRunningAsPWA) {
      // Already in PWA mode, navigate directly to login
      window.location.href = '/login';
    } else {
      // Not in PWA mode, open in new tab/window to trigger OS PWA interception
      window.open(window.location.origin + '/login', '_blank', 'noopener,noreferrer');
    }
  }, [isRunningAsPWA]);

  // Handle copy link to clipboard
  const handleCopyLink = useCallback(async (platform, link) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = link;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      setCopiedLink(platform);
      trackEvent('link_copied', { platform });

      if (copyLinkTimeoutRef.current) clearTimeout(copyLinkTimeoutRef.current);
      copyLinkTimeoutRef.current = setTimeout(() => setCopiedLink(null), 2000);
    } catch (error) {
      console.error('Copy failed:', error);
    }
  }, []);

  // Handle app download click
  const handleDownloadClick = useCallback((platform, link) => {
    trackEvent('download_clicked', {
      platform,
      device_type: deviceInfo?.deviceType,
    });

    if (platform === 'pwa' && deferredPrompt) {
      handlePWAInstall();
    } else {
      window.open(link, '_blank', 'noopener,noreferrer');
    }
  }, [deviceInfo, deferredPrompt, handlePWAInstall]);

  // Handle deep link (open installed app)
  const handleOpenApp = useCallback(async (platform) => {
    setCheckingApp(true);
    trackEvent('open_app_attempted', { platform });

    const deepLink = platform === 'ios'
      ? appDownloadConfig.ios.deepLink
      : appDownloadConfig.android.deepLink;

    try {
      const opened = await checkAppInstalledDeepLink(deepLink);

      if (!opened) {
        // App not installed, redirect to store
        const storeLink = platform === 'ios'
          ? appDownloadConfig.ios.appStoreUrl
          : appDownloadConfig.android.playStoreUrl;

        window.open(storeLink, '_blank');
        trackEvent('app_not_installed_redirect', { platform });
      } else {
        trackEvent('app_opened', { platform });
      }
    } catch (error) {
      console.error('Deep link error:', error);
    } finally {
      setCheckingApp(false);
    }
  }, []);

  // Get installation instructions for platform
  const getInstructions = (platform) => {
    // For PWA on iOS mobile, show iOS-specific instructions (Share -> Add to Home Screen)
    if (platform === 'pwa' && deviceInfo?.isIOS && !deviceInfo?.isDesktop) {
      return [
        Messages.download_pwa_ios_instruction_1,
        Messages.download_pwa_ios_instruction_2,
        Messages.download_pwa_ios_instruction_3,
        Messages.download_pwa_ios_instruction_4,
      ];
    }

    const instructionsMap = {
      ios: [
        Messages.download_ios_instruction_1,
        Messages.download_ios_instruction_2,
        Messages.download_ios_instruction_3,
      ],
      android: [
        Messages.download_android_instruction_1,
        Messages.download_android_instruction_2,
        Messages.download_android_instruction_3,
      ],
      pwa: [
        Messages.download_pwa_instruction_1,
        Messages.download_pwa_instruction_2,
        Messages.download_pwa_instruction_3,
      ],
    };

    return instructionsMap[platform] || [];
  };

  // Render QR code with loading state
  const renderQRCode = (link, platform, size = 130) => (
    <div className="download-app__qr-wrapper">
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className="download-app__qr-link"
        onClick={() => trackEvent('qr_code_clicked', { link })}
      >
        <QRCodeSVG
          value={link}
          size={size}
          level="H"
          marginSize={4}
          className="download-app__qr-code"
        />
      </a>
      <p className="download-app__scan-text">
        <Text content={Messages.download_scan_or_click_qr} />
      </p>
      <Button
        color="secondary"
        size="sm"
        outline
        onClick={() => handleCopyLink(platform, link)}
        className="download-app__button download-app__button--copy download-app__button--copy-qr"
      >
        {copiedLink === platform ? (
          <>
            <i className="fa fa-check"></i>
            {' '}
            <Text content={Messages.download_link_copied} />
          </>
        ) : (
          <>
            <i className="fa fa-copy"></i>
            {' '}
            <Text content={Messages.download_copy_link} />
          </>
        )}
      </Button>
    </div>
  );

  // Render platform icon
  const renderPlatformIcon = (platform) => {
    switch (platform) {
      case 'ios':
        return (
          <svg className="download-app__icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
          </svg>
        );
      case 'android':
        return (
          <svg className="download-app__icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24c-2.86-1.21-6.08-1.21-8.94 0L5.65 5.67c-.19-.28-.55-.37-.83-.22-.3.16-.42.54-.26.85l1.84 3.18C2.92 11.03 1 14.22 1 17.8h22c0-3.58-1.92-6.77-5.4-8.32zM8.06 15.2c-.66 0-1.2-.54-1.2-1.2s.54-1.2 1.2-1.2 1.2.54 1.2 1.2-.54 1.2-1.2 1.2zm7.88 0c-.66 0-1.2-.54-1.2-1.2s.54-1.2 1.2-1.2 1.2.54 1.2 1.2-.54 1.2-1.2 1.2z" />
          </svg>
        );
      case 'pwa':
        return (
          <svg className="download-app__icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 18c1.1 0 1.99-.9 1.99-2L22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z" />
          </svg>
        );
      default:
        return null;
    }
  };

  const renderDownloadOption = (platform, link, title, description, isPrimary = false) => {
    const config = appDownloadConfig[platform];

    if (!config || !config.enabled) return null;

    const hasInstructions = appDownloadConfig.features.showInstructions && getInstructions(platform).length > 0;

    return (
      <Col md={isPrimary ? 12 : 4} lg={isPrimary ? 12 : 4} className="download-app__option-col">
        <Card className={`download-app__card ${isPrimary ? 'download-app__card--primary' : ''}`}>
          <CardBody className="download-app__card-body">
            <div className="download-app__card-header">
              <div className="download-app__icon-wrapper">
                {renderPlatformIcon(platform)}
              </div>
              <div className="download-app__title-wrapper">
                <h2 className="download-app__title">
                  <Text content={title} />
                </h2>
                <p className="download-app__description">
                  <Text content={description} />
                </p>
              </div>
            </div>

            {/* Content Section */}
            <div className="download-app__content">
              {/* Show "App Installed" badge */}
              {appInstalled && platform !== 'pwa' && deviceInfo?.deviceType === platform && (
                <Alert color="success" className="download-app__installed-badge">
                  <i className="fa fa-check-circle"></i>
                  {' '}
                  <Text content={Messages.download_app_installed} />
                </Alert>
              )}

              {/* QR Code Section */}
              {renderQRCode(link, platform, isPrimary ? 140 : 130)}

              {/* "OR" Separator - PWA only */}
              {platform === 'pwa' && (
                <div className="download-app__separator">
                  <span className="download-app__separator-text">
                    <Text content={Messages.or} />
                  </span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="download-app__actions">
              {/* Primary Download/Install Button */}
              {platform === 'pwa' && (deviceInfo?.isIOS || deviceInfo?.isSafari) ? (
                // iOS or Safari: Show modal with instructions (Safari doesn't support beforeinstallprompt)
                <Button
                  color="primary"
                  size={isPrimary ? 'lg' : 'md'}
                  block
                  onClick={() => { setInstructionsModalPlatform('pwa'); setShowInstructionsModal(true); }}
                  className="download-app__button download-app__button--primary"
                >
                  <i className="fa fa-info-circle"></i>
                  {' '}
                  <Text content={Messages.download_how_to_install} />
                </Button>
              ) : platform === 'pwa' && checkingPwaInstalled ? (
                // Loading state while checking PWA installation
                <Button
                  color="primary"
                  size={isPrimary ? 'lg' : 'md'}
                  block
                  disabled
                  className="download-app__button download-app__button--primary"
                >
                  <Spinner size="sm" className="download-app__button-spinner" />
                  <Text content={Messages.loading} />
                </Button>
              ) : platform === 'pwa' && pwaInstalled ? (
                // PWA is installed: Show Open App or Go to Login button
                <Button
                  color="success"
                  size={isPrimary ? 'lg' : 'md'}
                  block
                  onClick={handleOpenPWA}
                  className="download-app__button download-app__button--success"
                >
                  <i className={isRunningAsPWA ? "fa fa-sign-in" : "fa fa-external-link"}></i>
                  {' '}
                  <Text content={isRunningAsPWA ? Messages.go_to_login : Messages.download_open_pwa_app} />
                </Button>
              ) : platform === 'pwa' ? (
                // Desktop: Install PWA
                <Button
                  color="primary"
                  size={isPrimary ? 'lg' : 'md'}
                  block
                  onClick={handlePWAInstall}
                  disabled={installStatus === 'installing'}
                  className="download-app__button download-app__button--primary"
                >
                  {installStatus === 'installing' ? (
                    <>
                      <Spinner size="sm" className="download-app__button-spinner" />
                      <Text content={Messages.download_installing} />
                    </>
                  ) : (
                    <>
                      <i className="fa fa-download"></i>
                      {' '}
                      <Text content={Messages.install_pwa} />
                    </>
                  )}
                </Button>
              ) : appInstalled && platform !== 'pwa' && deviceInfo?.deviceType === platform ? (
                <Button
                  color="success"
                  size={isPrimary ? 'lg' : 'md'}
                  block
                  onClick={() => handleOpenApp(platform)}
                  disabled={checkingApp}
                  className="download-app__button download-app__button--success"
                >
                  {checkingApp ? (
                    <>
                      <Spinner size="sm" className="download-app__button-spinner" />
                      <Text content={Messages.download_opening} />
                    </>
                  ) : (
                    <>
                      <i className="fa fa-external-link"></i>
                      {' '}
                      <Text content={Messages.download_open_app} />
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  color="primary"
                  size={isPrimary ? 'lg' : 'md'}
                  block
                  onClick={() => handleDownloadClick(platform, link)}
                  className="download-app__button download-app__button--primary"
                >
                  <i className={platform === 'ios' ? 'fa fa-apple' : 'fa fa-android'}></i>
                  {' '}
                  <Text content={platform === 'ios' ? Messages.download_open_app_store : Messages.download_open_google_play} />
                </Button>
              )}
            </div>

            {/* Installation Instructions */}
            {hasInstructions && (
              <div className="download-app__instructions">
                <Button
                  color="link"
                  className="download-app__instructions-toggle"
                  onClick={() => { setInstructionsModalPlatform(platform); setShowInstructionsModal(true); }}
                >
                  <i className="fa fa-question-circle"></i>
                  {' '}
                  <Text content={Messages.download_how_to_install} />
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
      </Col>
    );
  };

  // Loading state
  if (!deviceInfo) {
    return (
      <div className="download-app download-app--loading">
        <Spinner color="primary" size="lg" />
      </div>
    );
  }

  const { ios, android, pwa } = appDownloadConfig;
  const primaryPlatform = deviceInfo.deviceType;
  const showAllOptions = deviceInfo.isDesktop;
  const simulationMode = getSimulationMode();
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  return (
    <main id="main-layout" className="download-app">
      {/* Simulation Control Panel - Localhost Only */}
      {isLocalhost && (
        <div className="download-app__simulation-banner">
          <Container>
            <div className="download-app__simulation-content">
              <span className="download-app__simulation-label">
                <i className="fa fa-flask"></i>
                {' '}
                {simulationMode ? (
                  <>Simulating: <strong>{simulationMode.toUpperCase()}</strong></>
                ) : (
                  <>Device: <strong>{deviceInfo.deviceType.toUpperCase()}</strong> (actual)</>
                )}
              </span>
              <div className="download-app__simulation-controls">
                <Button size="sm" color={simulationMode === 'ios' ? 'primary' : 'light'} onClick={() => setDeviceSimulation('ios')}>iPhone</Button>
                <Button size="sm" color={simulationMode === 'ipad' ? 'primary' : 'light'} onClick={() => setDeviceSimulation('ipad')}>iPad</Button>
                <Button size="sm" color={simulationMode === 'android' ? 'primary' : 'light'} onClick={() => setDeviceSimulation('android')}>Android</Button>
                <Button size="sm" color={simulationMode === 'desktop' || (!simulationMode && deviceInfo.isDesktop) ? 'primary' : 'light'} onClick={simulationMode ? clearDeviceSimulation : undefined}>Desktop</Button>
                {simulationMode && !deviceInfo.isDesktop && (
                  <Button size="sm" color="danger" onClick={clearDeviceSimulation}>Clear</Button>
                )}
              </div>
            </div>
          </Container>
        </div>
      )}

      <Container className="download-app__container">
        {/* Confirmation Message */}
        {userConfirmation && (
          <Row className="mb-3">
            <Col>
              <Alert color="success" className="text-center">
                {confirmSuccessMessage || <Text content={Messages.user_activated_success} />}
              </Alert>
            </Col>
          </Row>
        )}

        {/* Header */}
        <Row className="download-app__header-row">
          <Col>
            <div className="download-app__header">
              <img
                src="/img/favicon.png"
                alt="App Icon"
                className="download-app__app-icon"
              />
              <h1 className="download-app__main-title">
                <Text content={Messages.download_main_title} />
              </h1>
              <p className="download-app__subtitle">
                <Text content={Messages.download_subtitle} />
              </p>
            </div>
          </Col>
        </Row>

        {/* Install Status Messages */}
        {(installStatus || isRunningAsPWA) && (
          <Row className="mb-3">
            <Col>
              {(installStatus === 'success' || isRunningAsPWA) && (
                <Alert color="success" className="download-app__status-alert">
                  <i className="fa fa-check-circle"></i>
                  {' '}
                  <Text content={Messages.download_install_success} />
                  {' '}
                  <a href="/login" className="download-app__login-link">
                    <Text content={Messages.go_to_login} />
                  </a>
                </Alert>
              )}
              {installStatus === 'error' && (
                <Alert color="danger" className="download-app__status-alert">
                  <i className="fa fa-exclamation-circle"></i>
                  {' '}
                  <Text content={Messages.download_install_error} />
                </Alert>
              )}
            </Col>
          </Row>
        )}

        {/* Primary Platform (Mobile Only) */}
        {!showAllOptions && (
          <Row className="download-app__primary-row">
            {primaryPlatform === 'ios' && ios.enabled &&
              renderDownloadOption(
                'ios',
                ios.appStoreUrl,
                Messages.download_ios_title,
                Messages.download_ios_description,
                true
              )
            }
            {primaryPlatform === 'android' && android.enabled &&
              renderDownloadOption(
                'android',
                android.playStoreUrl,
                Messages.download_android_title,
                Messages.download_android_description,
                true
              )
            }
          </Row>
        )}

        {/* All Options (Desktop only) */}
        {showAllOptions && (
          <Row className="download-app__options-row">
            {ios.enabled &&
              renderDownloadOption(
                'ios',
                ios.appStoreUrl,
                Messages.download_ios_title,
                Messages.download_ios_description
              )
            }

            {pwa.enabled &&
              renderDownloadOption(
                'pwa',
                pwa.url,
                Messages.download_pwa_title,
                Messages.download_pwa_description
              )
            }

            {android.enabled &&
              renderDownloadOption(
                'android',
                android.playStoreUrl,
                Messages.download_android_title,
                Messages.download_android_description
              )
            }
          </Row>
        )}

        {/* Show PWA option on mobile */}
        {!showAllOptions && pwa.enabled && (
          <Row className="download-app__other-platforms-row">
            <Col>
              <h2 className="download-app__section-title">
                <Text content={Messages.download_other_platforms} />
              </h2>
            </Col>
          </Row>
        )}

        {!showAllOptions && pwa.enabled && (
          <Row className="download-app__options-row">
            {renderDownloadOption(
              'pwa',
              pwa.url,
              Messages.download_pwa_title,
              Messages.download_pwa_description,
              true
            )}
          </Row>
        )}

        {/* Footer */}
        <Row className="download-app__footer-row">
          <Col>
            <div className="download-app__footer">
              <p className="download-app__footer-text">
                <Text content={Messages.download_footer_text} />
              </p>
              {userConfirmation &&
                <p className="download-app__reset-password">
                  <Text content={Messages.dont_rem_pwd} />
                  {' '}
                  <a href="/forgot-password" className="download-app__reset-password-link">
                    <Text content={Messages.here} />
                  </a>
                  {' '}
                  <Text content={Messages.to_reset} />
                </p>}
            </div>
          </Col>
        </Row>
      </Container>

      {/* Instructions Modal */}
      <Modal
        isOpen={showInstructionsModal}
        toggle={() => setShowInstructionsModal(false)}
        className="download-app__modal"
        centered
        size="lg"
      >
        <ModalHeader toggle={() => setShowInstructionsModal(false)}>
          <Text content={Messages.download_how_to_install} />
        </ModalHeader>
        <ModalBody>
          {/* iOS App Store Instructions */}
          {instructionsModalPlatform === 'ios' && (
            <ol className="download-app__modal-instructions">
              <li><Text content={Messages.download_ios_instruction_1} /></li>
              <li><Text content={Messages.download_ios_instruction_2} /></li>
              <li><Text content={Messages.download_ios_instruction_3} /></li>
            </ol>
          )}

          {/* Android Play Store Instructions */}
          {instructionsModalPlatform === 'android' && (
            <ol className="download-app__modal-instructions">
              <li><Text content={Messages.download_android_instruction_1} /></li>
              <li><Text content={Messages.download_android_instruction_2} /></li>
              <li><Text content={Messages.download_android_instruction_3} /></li>
            </ol>
          )}

          {/* PWA Instructions - iOS Safari */}
          {instructionsModalPlatform === 'pwa' && deviceInfo?.isIOS && (
            <>
              <div className="download-app__modal-video">
                <iframe
                  width="100%"
                  height="315"
                  src="https://www.youtube.com/embed/rXzADglUEZA"
                  title="How to install PWA on iOS"
                  style={{ border: 'none' }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <ol className="download-app__modal-instructions">
                <li><Text content={Messages.download_pwa_ios_instruction_1} /></li>
                <li><Text content={Messages.download_pwa_ios_instruction_2} /></li>
                <li><Text content={Messages.download_pwa_ios_instruction_3} /></li>
                <li><Text content={Messages.download_pwa_ios_instruction_4} /></li>
              </ol>
            </>
          )}

          {/* PWA Instructions - Chrome/Desktop */}
          {instructionsModalPlatform === 'pwa' && !deviceInfo?.isIOS && (
            <ol className="download-app__modal-instructions">
              <li><Text content={Messages.download_pwa_chrome_instruction_1} /></li>
              <li><Text content={Messages.download_pwa_chrome_instruction_2} /></li>
              <li><Text content={Messages.download_pwa_chrome_instruction_3} /></li>
              <li><Text content={Messages.download_pwa_chrome_instruction_4} /></li>
            </ol>
          )}
        </ModalBody>
      </Modal>
    </main>
  );
};

export default DownloadApp;
